const DESKTOP_QUERY = '(min-width: 48rem)'
const SWIPE_DISTANCE_THRESHOLD = 50
// Decide who owns the gesture early, before the browser commits to vertical scrolling.
const SWIPE_DIRECTION_THRESHOLD = 8
// Allow ordinary vertical drift without turning predominantly vertical gestures into page changes.
const SWIPE_MAX_ANGLE = 55
// Distinguish an intentional canvas click from mouse movement or an attempted drag.
const CANVAS_CLICK_MOVE_THRESHOLD = 8
// Stop readable pages from shrinking indefinitely when the browser window is short.
const MIN_PAGE_HEIGHT = 320
// Cap the complete flipbook, including controls and links, below the window height.
const MAX_FLIPBOOK_VIEWPORT_RATIO = 0.9
// Avoid multiplying large canvas memory use on very dense displays.
const MAX_PIXEL_RATIO = 2
const ZOOM_STEP = 0.25
const MAX_ZOOM = 3
// Bound backing-store memory even when a large fitted page is zoomed on a dense display.
const MAX_CANVAS_PIXELS = 8_000_000
const MAX_CANVAS_EDGE = 8192
const DOWNLOAD_EVENT = 'hei:pdfFlipbookDownload'
const PAGE_CHANGE_EVENT = 'hei:pdfFlipbookPageChange'
let pdfjsPromise = null

const loadPdfJs = () => {
    if (pdfjsPromise === null) {
        pdfjsPromise = Promise.all([
            import('pdfjs-dist'),
            // Vite emits the worker as a versioned production asset and returns its URL.
            import('pdfjs-dist/build/pdf.worker.min.mjs?url'),
        ]).then(([pdfjs, worker]) => {
            pdfjs.GlobalWorkerOptions.workerSrc = worker.default

            return pdfjs
        })
    }

    return pdfjsPromise
}

class PdfFlipbook {
    constructor(root) {
        this.root = root
        this.url = root.dataset.pdfUrl
        this.title = root.dataset.pdfTitle
        this.displayContext = root.dataset.pdfContext
        this.isStandalone = this.displayContext === 'standalone'
        this.viewport = root.querySelector('[data-pdf-flipbook-viewport]')
        this.pages = root.querySelector('[data-pdf-flipbook-pages]')
        this.loading = root.querySelector('[data-pdf-flipbook-loading]')
        this.controls = root.querySelector('[data-pdf-flipbook-controls]')
        this.previousButton = root.querySelector('[data-pdf-flipbook-previous]')
        this.nextButton = root.querySelector('[data-pdf-flipbook-next]')
        this.currentPageElement = root.querySelector('[data-pdf-flipbook-current-page]')
        this.pageCountElement = root.querySelector('[data-pdf-flipbook-page-count]')
        this.status = root.querySelector('[data-pdf-flipbook-status]')
        this.instructions = root.querySelector('[data-pdf-flipbook-instructions]')
        this.zoomControls = root.querySelector('[data-pdf-flipbook-zoom]')
        this.zoomInButton = root.querySelector('[data-pdf-flipbook-zoom-in]')
        this.zoomOutButton = root.querySelector('[data-pdf-flipbook-zoom-out]')
        this.fitButton = root.querySelector('[data-pdf-flipbook-fit]')
        this.zoomLevel = root.querySelector('[data-pdf-flipbook-zoom-level]')
        this.downloadLink = root.querySelector('[data-pdf-flipbook-download]')
        // 100% means the responsive fitted size, not the PDF's physical print size.
        this.zoom = 1
        this.isReady = false
        this.hasError = false
        this.scrollResetPending = false
        this.zoomAnchor = null
        this.currentPage = 1
        this.pageCount = 0
        this.document = null
        this.logicalPages = []
        this.isRendering = false
        // Track the latest requested page set and the PDF.js tasks that can be
        // cancelled when a resize makes their canvas dimensions obsolete.
        this.renderAfterCurrent = false
        this.renderRequestId = 0
        this.activeRenderTasks = new Set()
        // PDF.js caches each operator list, so remember scheduled source pages
        // and avoid repeating the same background work after resize or navigation.
        this.preloadedSourcePages = new Set()
        // Only explicit navigation sets a direction; loading and resizing stay motionless.
        this.transitionDirection = null
        // Store one primary pointer so multi-touch cannot trigger competing navigation.
        this.swipeStart = null
        this.panStart = null
        this.canvasClickStart = null
        this.resizeObserver = null
        this.toolbarObserver = null
        this.resizeFrame = null
        this.stageWidth = 0
        this.stageHeight = 0
        this.viewportHeight = 0
        this.desktopLayout = false
        this.pageWidth = 0
        this.pageHeight = 0
        this.toolbarHeight = 0
    }

    async init() {
        this.downloadLink?.addEventListener('click', () => this.emitDownload())
        this.observeToolbar()

        if (!this.url || !this.viewport || !this.pages) {
            return
        }

        try {
            const pdfjs = await loadPdfJs()
            this.document = await pdfjs.getDocument({
                url: this.url,
                // Avoid streaming the complete PDF in the initial response. PDF.js
                // will request only the ranges needed for document metadata and rendering.
                disableStream: true,
                // Do not continue downloading ranges that have not been requested yet.
                disableAutoFetch: true,
            }).promise
            const firstPage = await this.document.getPage(1)

            // Render the cover before inspecting every remaining source page.
            this.logicalPages = this.getLogicalPagesForSourcePage(1, firstPage)
            // Keep the count unknown so the first canvas does not announce a provisional total.
            this.pageCount = 0

            this.previousButton?.addEventListener('click', () => this.showPreviousPages('previous'))
            this.nextButton?.addEventListener('click', () => this.showNextPages('next'))
            this.zoomInButton?.addEventListener('click', () => this.setZoom(this.zoom + ZOOM_STEP))
            this.zoomOutButton?.addEventListener('click', () => this.setZoom(this.zoom - ZOOM_STEP))
            this.fitButton?.addEventListener('click', () => this.setZoom(1))
            this.viewport.addEventListener('keydown', (event) => this.handleKeydown(event))
            this.viewport.addEventListener('pointerdown', (event) => this.handlePointerDown(event))
            this.viewport.addEventListener('pointermove', (event) => this.movePan(event))
            this.viewport.addEventListener('pointerup', (event) => {
                this.endPan(event)
                this.finishCanvasClick(event)
            })
            this.viewport.addEventListener('pointercancel', (event) => {
                this.endPan(event)
                this.cancelCanvasClick(event)
            })
            this.viewport.addEventListener('lostpointercapture', (event) => {
                this.endPan(event)
                this.cancelCanvasClick(event)
            })
            // A native pan can cancel the Pointer Events stream before release. Touch Events let us
            // establish horizontal intent early and then prevent scrolling for that gesture only.
            this.viewport.addEventListener('touchstart', (event) => this.startSwipe(event), { passive: true })
            this.viewport.addEventListener('touchmove', (event) => this.moveSwipe(event), { passive: false })
            this.viewport.addEventListener('touchend', (event) => this.finishSwipe(event))
            this.viewport.addEventListener('touchcancel', () => this.cancelSwipe())
            // Switching windows can hide the release event; don't leave a drag active on return.
            window.addEventListener('blur', () => this.cancelGestures())

            this.loading.hidden = true
            this.pages.hidden = false

            this.updateStageSize()

            if (!await this.render()) {
                return
            }

            this.logicalPages = await this.buildLogicalPages(firstPage)
            this.pageCount = this.logicalPages.length
            this.controls.hidden = false
            if (this.zoomControls) {
                this.zoomControls.hidden = false
            }

            this.updateStageSize()

            if (!await this.render()) {
                return
            }

            this.observeStageSize()
            this.isReady = true
            this.updateControls()
            this.root.setAttribute('aria-busy', 'false')
        } catch (error) {
            this.showError()
            console.error('Unable to initialize PDF flipbook.', error)
        }
    }

    emitDownload() {
        this.emit(DOWNLOAD_EVENT, {
            title: this.title,
            fileType: 'pdf',
            displayContext: this.displayContext,
        })
    }

    emitPageChange(navigationMethod) {
        const pageNumbers = this.getVisiblePageNumbers()

        this.emit(PAGE_CHANGE_EVENT, {
            title: this.title,
            pageNumber: pageNumbers[0],
            pageEnd: pageNumbers.at(-1),
            totalPages: this.pageCount,
            navigationMethod,
            displayContext: this.displayContext,
        })
    }

    emit(name, detail) {
        this.root.dispatchEvent(new CustomEvent(name, {
            bubbles: true,
            detail,
        }))
    }

    observeToolbar() {
        const toolbar = this.root.querySelector('.pdf-flipbook__toolbar')
        const actions = toolbar?.querySelector('.pdf-flipbook__actions')

        if (!toolbar || !actions || !this.controls) {
            return
        }

        // Measure the buttons rather than the expanding action wrapper, so a wrapped
        // row can return to right alignment when space becomes available again.
        this.toolbarObserver = new ResizeObserver(() => {
            const buttons = [...actions.children].filter((button) => !button.hidden)
            const actionGap = parseFloat(getComputedStyle(actions).columnGap) || 0
            const toolbarGap = parseFloat(getComputedStyle(toolbar).columnGap) || 0
            const actionWidth = buttons.reduce((width, button) => width + button.getBoundingClientRect().width, 0)
                + Math.max(0, buttons.length - 1) * actionGap
            // Flex retains a gap on each side of navigation even when the balancing spacer shrinks to zero.
            const requiredWidth = this.controls.getBoundingClientRect().width + actionWidth + toolbarGap * 2

            toolbar.toggleAttribute('data-actions-wrapped',
                buttons.length > 0 && !this.controls.hidden && requiredWidth > toolbar.clientWidth)

            // Wrapping zoom controls changes the space left for the fitted page, even without a window resize.
            const toolbarHeight = Math.round(toolbar.getBoundingClientRect().height)
            if (toolbarHeight !== this.toolbarHeight) {
                this.toolbarHeight = toolbarHeight
                if (this.isReady && !this.hasError) {
                    this.scheduleResize()
                }
            }
        })

        this.toolbarObserver.observe(toolbar)
        this.toolbarObserver.observe(this.controls)
        for (const action of actions.children) {
            this.toolbarObserver.observe(action)
        }
    }

    usesSpreadLayout() {
        // Keep the cover centered; following logical pages form desktop spreads.
        return this.isDesktopLayout() && this.currentPage > 1
    }

    isDesktopLayout() {
        return window.matchMedia(DESKTOP_QUERY).matches
    }

    getLogicalPagesForSourcePage(sourcePageNumber, page) {
        const viewport = page.getViewport({ scale: 1 })

        // Some PDFs export two book pages inside one landscape PDF page.
        // Split those source spreads into left and right logical pages so
        // desktop can show the spread and mobile can show one page at a time.
        if (viewport.width > viewport.height) {
            return [
                {
                    sourcePageNumber,
                    crop: 'left',
                    width: viewport.width / 2,
                    height: viewport.height,
                },
                {
                    sourcePageNumber,
                    crop: 'right',
                    width: viewport.width / 2,
                    height: viewport.height,
                },
            ]
        }

        return [{
            sourcePageNumber,
            crop: 'full',
            width: viewport.width,
            height: viewport.height,
        }]
    }

    async buildLogicalPages(firstPage) {
        const sourcePages = await Promise.all(
            Array.from({ length: this.document.numPages }, async (_, index) => {
                const sourcePageNumber = index + 1
                const page = sourcePageNumber === 1
                    ? firstPage
                    : await this.document.getPage(sourcePageNumber)

                return this.getLogicalPagesForSourcePage(sourcePageNumber, page)
            }),
        )

        return sourcePages.flat()
    }

    getFirstVisiblePageNumber() {
        // Derive the spread start without replacing the exact page remembered on mobile.
        if (this.usesSpreadLayout() && this.currentPage % 2 !== 0) {
            return this.currentPage - 1
        }

        return this.currentPage
    }

    getVisiblePageNumbers() {
        const firstPageNumber = this.getFirstVisiblePageNumber()
        const pageNumbers = [firstPageNumber]

        if (this.usesSpreadLayout() && firstPageNumber < this.pageCount) {
            pageNumbers.push(firstPageNumber + 1)
        }

        return pageNumbers
    }

    getNextPageNumbers() {
        const firstPageNumber = this.getVisiblePageNumbers().at(-1) + 1
        // Match the current layout so desktop warms the next spread while mobile
        // fetches only the next single page.
        const preloadCount = this.isDesktopLayout() ? 2 : 1

        return Array.from(
            { length: preloadCount },
            (_, index) => firstPageNumber + index,
        ).filter((pageNumber) => pageNumber <= this.pageCount)
    }

    getWidthLimitedPageWidth() {
        // Measure the viewport, not the page stage: the latter grows beyond it when zoomed.
        const style = window.getComputedStyle(this.viewport)
        const availableWidth = this.viewport.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight)
        const maxWidth = parseFloat(window.getComputedStyle(this.pages).maxWidth) || Infinity
        const stageWidth = Math.min(availableWidth, maxWidth)

        return this.isDesktopLayout() ? stageWidth / 2 : stageWidth
    }

    getViewportHeight() {
        return window.visualViewport?.height || window.innerHeight
    }

    updateStageSize() {
        const maxPageRatio = Math.max(
            ...this.logicalPages.map((page) => page.height / page.width),
        )

        if (this.isStandalone) {
            // CSS reserves the heading and controls first. Fit into the remaining
            // viewport content box, subtracting its padding, without the block's height minimum.
            const style = window.getComputedStyle(this.viewport)
            const availableWidth = this.viewport.clientWidth
                - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight)
            const availableHeight = this.viewport.clientHeight
                - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom)

            // Reserve both desktop page slots even for the cover, keeping its scale
            // consistent with the spreads that follow instead of resizing on navigation.
            const fitPageWidth = Math.max(1, Math.floor(Math.min(
                availableWidth / (this.isDesktopLayout() ? 2 : 1),
                availableHeight / maxPageRatio,
            )))
            this.pageWidth = Math.floor(fitPageWidth * this.zoom)
            this.pageHeight = Math.ceil(this.pageWidth * maxPageRatio)

            return
        }

        // Subtract the fixed UI around the page stage so the height cap applies
        // to the complete flipbook rather than only to its rendered canvases.
        const style = window.getComputedStyle(this.viewport)
        const verticalPadding = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom)
        const flipbookHeight = this.root.getBoundingClientRect().height
        const flipbookChromeHeight = Math.max(0, flipbookHeight - this.viewport.getBoundingClientRect().height) + verticalPadding
        const maxFlipbookHeight = this.getViewportHeight() * MAX_FLIPBOOK_VIEWPORT_RATIO
        const maxStageHeight = Math.max(1, maxFlipbookHeight - flipbookChromeHeight)
        const widthLimitedPageWidth = Math.floor(this.getWidthLimitedPageWidth())

        // Convert the minimum page height to a width so the PDF keeps its aspect
        // ratio. Available width remains the hard limit on very narrow screens.
        const minimumPageWidth = Math.min(
            widthLimitedPageWidth,
            Math.ceil(MIN_PAGE_HEIGHT / maxPageRatio),
        )

        // Prefer the window-height cap, but let the readable minimum take precedence.
        // Whole CSS pixels keep adjacent canvas edges aligned with their grid tracks.
        const fitPageWidth = Math.max(
            1,
            minimumPageWidth,
            Math.floor(Math.min(widthLimitedPageWidth, maxStageHeight / maxPageRatio)),
        )

        this.viewport.style.setProperty('--pdf-flipbook-viewport-height', `${Math.ceil(fitPageWidth * maxPageRatio) + verticalPadding}px`)
        this.pageWidth = Math.floor(fitPageWidth * this.zoom)
        this.pageHeight = Math.ceil(this.pageWidth * maxPageRatio)
    }

    observeStageSize() {
        // Observe the available space, not the zoomed canvases, to avoid resize/render feedback.
        const observedStage = this.viewport
        this.stageWidth = Math.round(observedStage.getBoundingClientRect().width)
        this.stageHeight = 0
        this.viewportHeight = Math.round(this.getViewportHeight())
        this.desktopLayout = this.isDesktopLayout()

        this.resizeObserver = new ResizeObserver(([entry]) => {
            const stageWidth = Math.round(entry.contentRect.width)
            const stageHeight = this.isStandalone ? Math.round(entry.contentRect.height) : 0
            const desktopLayout = this.isDesktopLayout()

            if (stageWidth === this.stageWidth && stageHeight === this.stageHeight && desktopLayout === this.desktopLayout) {
                return
            }

            this.stageWidth = stageWidth
            this.stageHeight = stageHeight
            this.desktopLayout = desktopLayout

            this.scheduleResize()
        })

        // The standalone viewport can also change height when its title wraps or fonts load.
        this.resizeObserver.observe(observedStage)

        // ResizeObserver catches width and breakpoint changes; VisualViewport also
        // catches height-only changes such as orientation or browser chrome updates.
        const viewport = window.visualViewport || window
        viewport.addEventListener('resize', () => {
            const viewportHeight = Math.round(this.getViewportHeight())

            if (viewportHeight === this.viewportHeight) {
                return
            }

            this.viewportHeight = viewportHeight
            this.scheduleResize()
        })
    }

    scheduleResize() {
        window.cancelAnimationFrame(this.resizeFrame)
        this.resizeFrame = window.requestAnimationFrame(() => this.handleResize())
    }

    async handleResize() {
        if (this.hasError) {
            return
        }
        // A responsive rerender changes dimensions, not the logical reading direction.
        this.transitionDirection = null
        this.updateStageSize()
        await this.render()
    }

    async setZoom(zoom) {
        if (!this.isReady || this.hasError || this.isRendering) {
            return
        }

        const nextZoom = Math.max(1, Math.min(MAX_ZOOM, zoom))
        if (nextZoom === this.zoom) {
            return
        }

        // Store the viewer center as a fraction of the current stage. After rendering,
        // map that point onto the new stage so zooming does not jump to a different area.
        const pageRect = this.pages.getBoundingClientRect()
        const viewportRect = this.viewport.getBoundingClientRect()
        this.zoomAnchor = {
            x: (viewportRect.left + this.viewport.clientWidth / 2 - pageRect.left) / pageRect.width,
            y: (viewportRect.top + this.viewport.clientHeight / 2 - pageRect.top) / pageRect.height,
        }
        this.scrollResetPending = nextZoom === 1
        this.cancelSwipe()
        this.zoom = nextZoom
        this.transitionDirection = null
        this.updateStageSize()
        await this.render()
    }

    async render() {
        if (!this.document) {
            return
        }

        // Pointer positions belong to the outgoing layout, not the resized or newly rendered pages.
        this.cancelGestures()

        // Invalidate the previous request before cancelling it so any task that
        // finishes during cancellation still knows its canvas is stale.
        this.renderRequestId += 1
        this.cancelActiveRenders()

        if (this.isRendering) {
            this.renderAfterCurrent = true
            return
        }

        this.isRendering = true
        this.updateControls()

        try {
            // A resize during an awaited PDF.js render queues one more pass. Each
            // pass reads the latest page and dimensions rather than replaying old state.
            do {
                const requestId = this.renderRequestId

                this.renderAfterCurrent = false

                try {
                    await this.renderPageSet(requestId)
                } catch (error) {
                    // PDF.js rejects a render task when cancel() stops obsolete work.
                    if (!this.isRenderCancellation(error)) {
                        // Navigation and resize renders can fail after initialization has finished.
                        this.showError()
                        console.error('Unable to render PDF flipbook.', error)

                        return false
                    }
                }
            } while (this.renderAfterCurrent)
        } finally {
            this.isRendering = false
            this.updateControls()
        }

        return true
    }

    async renderPageSet(requestId) {
        const pageNumbers = this.getVisiblePageNumbers()
        const layout = pageNumbers.length === 1 ? 'single' : 'spread'
        const pageWidth = this.pageWidth || this.getWidthLimitedPageWidth()
        const pageHeight = this.pageHeight
        const canvases = await Promise.all(
            pageNumbers.map((pageNumber) => (
                this.renderPage(pageNumber, pageWidth, requestId)
            )),
        )

        if (requestId !== this.renderRequestId || canvases.some((canvas) => !canvas)) {
            return
        }

        const transitionDirection = this.transitionDirection

        this.transitionDirection = null

        // Each PDF.js render creates new canvases, so applying the class before
        // the atomic swap starts one entrance animation without retaining old pages.
        if (transitionDirection) {
            canvases.forEach((canvas) => canvas.classList.add('pdf-flipbook__page--entering'))
            this.pages.dataset.transitionDirection = transitionDirection
        } else {
            delete this.pages.dataset.transitionDirection
        }

        this.setCanvasActions(canvases, pageNumbers)

        // Swap the completed canvases and layout in one synchronous step so
        // the browser cannot paint the outgoing pages using the incoming layout.
        this.pages.style.setProperty('--pdf-flipbook-page-width', `${pageWidth}px`)
        this.pages.style.setProperty('--pdf-flipbook-stage-height', `${pageHeight}px`)
        this.pages.style.setProperty('--pdf-flipbook-spread-width', `${pageWidth * pageNumbers.length}px`)
        this.pages.style.setProperty('--pdf-flipbook-visible-pages', String(pageNumbers.length))
        this.root.toggleAttribute('data-pdf-zoomed', this.zoom > 1)
        this.pages.replaceChildren(...canvases)
        this.pages.dataset.pageLayout = layout
        // Restore position only after the new dimensions exist, or the browser would
        // clamp the requested scroll offsets against the outgoing, smaller canvases.
        if (this.scrollResetPending) {
            this.viewport.scrollLeft = 0
            this.viewport.scrollTop = 0
        } else if (this.zoomAnchor) {
            const pageRect = this.pages.getBoundingClientRect()
            const viewportRect = this.viewport.getBoundingClientRect()
            this.viewport.scrollLeft += pageRect.left - viewportRect.left
                + this.zoomAnchor.x * pageRect.width - this.viewport.clientWidth / 2
            this.viewport.scrollTop += pageRect.top - viewportRect.top
                + this.zoomAnchor.y * pageRect.height - this.viewport.clientHeight / 2
        }
        this.scrollResetPending = false
        this.zoomAnchor = null
        this.instructions.textContent = this.zoom > 1
            ? 'Drag or scroll to explore the enlarged PDF. Use the previous and next buttons to change pages.'
            : 'Use the left and right arrow keys, or swipe horizontally on a touchscreen, to change pages.'
        if (this.zoom > 1) {
            this.viewport.removeAttribute('aria-keyshortcuts')
        } else {
            this.viewport.setAttribute('aria-keyshortcuts', 'ArrowLeft ArrowRight')
        }
        this.updateStatus(pageNumbers)
        // Start lookahead only after the visible pages are ready, without delaying controls.
        void this.preloadNextPageSet()
    }

    async preloadNextPageSet() {
        if (this.pageCount === 0) {
            return
        }

        // Two logical book pages may be cropped from one landscape PDF page, so
        // preload each underlying source page only once.
        const sourcePageNumbers = new Set(
            this.getNextPageNumbers().map((pageNumber) => (
                this.logicalPages[pageNumber - 1].sourcePageNumber
            )),
        )

        await Promise.all(
            [...sourcePageNumbers].map(async (sourcePageNumber) => {
                if (this.preloadedSourcePages.has(sourcePageNumber)) {
                    return
                }

                this.preloadedSourcePages.add(sourcePageNumber)

                try {
                    const page = await this.document.getPage(sourcePageNumber)

                    // Warm PDF.js's drawing-data cache without allocating a hidden canvas.
                    await page.getOperatorList()
                } catch {
                    // Preloading is best-effort; let the normal render path retry and
                    // report an error only if the user actually requests this page.
                    this.preloadedSourcePages.delete(sourcePageNumber)
                }
            }),
        )
    }

    cancelActiveRenders() {
        this.activeRenderTasks.forEach((renderTask) => renderTask.cancel())
    }

    isRenderCancellation(error) {
        return error?.name === 'RenderingCancelledException'
    }

    async renderPage(pageNumber, availableWidth, requestId) {
        const logicalPage = this.logicalPages[pageNumber - 1]
        const page = await this.document.getPage(logicalPage.sourcePageNumber)

        // A newer resize or navigation request can supersede this page lookup
        // before PDF.js begins the more expensive canvas render.
        if (requestId !== this.renderRequestId) {
            return null
        }

        const baseViewport = page.getViewport({ scale: 1 })
        const isHalfPage = logicalPage.crop !== 'full'
        const sourceWidth = isHalfPage ? baseViewport.width / 2 : baseViewport.width
        const cssScale = availableWidth / sourceWidth
        const cssHeight = baseViewport.height * cssScale
        // Pixel area grows with the square of the scale. Lower density when either
        // memory or edge limits are reached, while retaining the requested CSS zoom.
        const pixelRatio = Math.min(
            window.devicePixelRatio || 1,
            MAX_PIXEL_RATIO,
            Math.sqrt(MAX_CANVAS_PIXELS / (availableWidth * cssHeight)),
            MAX_CANVAS_EDGE / Math.max(availableWidth, cssHeight),
        )
        const renderViewport = page.getViewport({ scale: cssScale * pixelRatio })
        const canvas = document.createElement('canvas')
        const context = canvas.getContext('2d')
        const renderWidth = isHalfPage ? renderViewport.width / 2 : renderViewport.width

        canvas.width = Math.floor(renderWidth)
        canvas.height = Math.floor(renderViewport.height)
        // CSS dimensions must not change when the backing-store resolution reaches its cap.
        canvas.style.width = `${availableWidth}px`
        canvas.style.height = `${Math.floor(cssHeight)}px`
        canvas.setAttribute('role', 'img')
        const pageLabel = this.pageCount > 0
            ? `Page ${pageNumber} of ${this.pageCount}`
            : `Page ${pageNumber}`

        canvas.setAttribute('aria-label', pageLabel)

        // A half-width canvas clips the source spread; shift only the right half into view.
        const transform = logicalPage.crop === 'right'
            ? [1, 0, 0, 1, -renderWidth, 0]
            : null

        const renderTask = page.render({
            canvasContext: context,
            transform,
            viewport: renderViewport,
        })

        this.activeRenderTasks.add(renderTask)

        try {
            await renderTask.promise

            return requestId === this.renderRequestId ? canvas : null
        } finally {
            this.activeRenderTasks.delete(renderTask)
        }
    }

    handlePointerDown(event) {
        this.canvasClickStart = null

        if (!event.isPrimary) {
            return
        }

        if (
            !this.isReady || this.hasError || this.isRendering
            || event.altKey || event.ctrlKey || event.metaKey
            || event.target.closest('a, button, input, select, textarea, [contenteditable], [role="button"]')
        ) {
            return
        }

        if (event.pointerType === 'mouse' && this.zoom > 1) {
            this.startPan(event)
        } else if (event.pointerType === 'mouse' && this.zoom === 1) {
            this.startCanvasClick(event)
        }
        // Touch gestures use Touch Events so horizontal intent can be locked before native scrolling.
    }

    startPan(event) {
        if (event.button !== 0 || this.panStart) {
            return
        }

        this.panStart = { pointerId: event.pointerId, x: event.clientX, y: event.clientY }
        // Continue receiving movement and release when a drag leaves the viewport.
        this.viewport.setPointerCapture(event.pointerId)
        this.root.setAttribute('data-pdf-panning', '')
        this.viewport.focus({ preventScroll: true })
        event.preventDefault()
    }

    movePan(event) {
        if (!this.panStart || event.pointerId !== this.panStart.pointerId) {
            return
        }
        if (!(event.buttons & 1)) {
            this.endPan(event)
            return
        }

        // Use incremental movement: after hitting a scroll boundary, reversing the
        // drag should move immediately rather than retrace the distance beyond the edge.
        this.viewport.scrollLeft -= event.clientX - this.panStart.x
        this.viewport.scrollTop -= event.clientY - this.panStart.y
        this.panStart.x = event.clientX
        this.panStart.y = event.clientY
        event.preventDefault()
    }

    endPan(event) {
        if (!this.panStart || (event && event.pointerId !== this.panStart.pointerId)) {
            return
        }

        const { pointerId } = this.panStart
        // Clear state before release, since releasing capture also fires lostpointercapture.
        this.panStart = null
        this.root.removeAttribute('data-pdf-panning')
        if (this.viewport.hasPointerCapture(pointerId)) {
            this.viewport.releasePointerCapture(pointerId)
        }
    }

    cancelGestures() {
        this.endPan()
        this.cancelSwipe()
        this.canvasClickStart = null
    }

    startCanvasClick(event) {
        if (event.button !== 0 || !event.target.closest('canvas[data-pdf-canvas-action]')) {
            return
        }

        this.canvasClickStart = {
            pointerId: event.pointerId,
            x: event.clientX,
            y: event.clientY,
        }
    }

    finishCanvasClick(event) {
        if (!this.canvasClickStart || event.pointerId !== this.canvasClickStart.pointerId) {
            return
        }

        const clickStart = this.canvasClickStart
        this.canvasClickStart = null
        const canvas = event.target.closest('canvas[data-pdf-canvas-action]')

        if (
            event.pointerType !== 'mouse'
            || this.zoom > 1
            || this.isRendering
            || !canvas
            || Math.hypot(event.clientX - clickStart.x, event.clientY - clickStart.y) > CANVAS_CLICK_MOVE_THRESHOLD
        ) {
            return
        }

        if (canvas.dataset.pdfCanvasAction === 'next') {
            void this.showNextPages('next')
            return
        }
        if (canvas.dataset.pdfCanvasAction === 'previous') {
            void this.showPreviousPages('previous')
            return
        }

        // Mobile shows one intermediate page, so split that canvas into two actions.
        const canvasRect = canvas.getBoundingClientRect()
        if (event.clientX < canvasRect.left + canvasRect.width / 2) {
            void this.showPreviousPages('previous')
        } else {
            void this.showNextPages('next')
        }
    }

    setCanvasActions(canvases, pageNumbers) {
        // Actions live on the fresh canvases so an inactive page—such as the
        // right side of a final spread—does not advertise or receive a click.
        if (this.zoom > 1 || this.pageCount <= 1) {
            return
        }

        if (this.currentPage === 1) {
            canvases[0].dataset.pdfCanvasAction = 'next'
            return
        }

        const isFinalSet = pageNumbers.at(-1) >= this.pageCount

        if (canvases.length === 1) {
            canvases[0].dataset.pdfCanvasAction = isFinalSet ? 'previous' : 'split'
            return
        }

        canvases[0].dataset.pdfCanvasAction = 'previous'
        // On a complete final spread, the right page has no remaining forward action.
        if (!isFinalSet) canvases[1].dataset.pdfCanvasAction = 'next'
    }

    cancelCanvasClick(event) {
        if (!this.canvasClickStart || event.pointerId !== this.canvasClickStart.pointerId) {
            return
        }

        this.canvasClickStart = null
    }

    startSwipe(event) {
        if (
            !this.isReady
            || this.hasError
            || this.isRendering
            || this.zoom > 1
            || event.touches.length !== 1
            || event.target.closest('a, button, input, select, textarea, [contenteditable], [role="button"]')
        ) {
            this.cancelSwipe()
            return
        }

        const touch = event.touches[0]

        this.swipeStart = {
            // Track the initiating finger so adding another finger safely hands control to pinch zoom.
            touchId: touch.identifier,
            x: touch.clientX,
            y: touch.clientY,
            direction: null,
        }
    }

    moveSwipe(event) {
        if (!this.swipeStart || this.zoom > 1 || event.touches.length !== 1) {
            this.cancelSwipe()
            return
        }

        const touch = Array.from(event.touches)
            .find(({ identifier }) => identifier === this.swipeStart.touchId)

        if (!touch) {
            this.cancelSwipe()
            return
        }

        if (this.swipeStart.direction === null) {
            const deltaX = touch.clientX - this.swipeStart.x
            const deltaY = touch.clientY - this.swipeStart.y

            if (Math.hypot(deltaX, deltaY) < SWIPE_DIRECTION_THRESHOLD) {
                return
            }

            const angle = Math.atan2(Math.abs(deltaY), Math.abs(deltaX)) * 180 / Math.PI

            // Measure from the horizontal axis: larger angles indicate page-scrolling intent.
            if (angle > SWIPE_MAX_ANGLE) {
                // A vertical gesture remains owned by the page and scrolls normally.
                this.cancelSwipe()
                return
            }

            this.swipeStart.direction = 'horizontal'
        }

        // Once horizontal intent is established, later vertical drift must not start a page scroll.
        if (event.cancelable) event.preventDefault()
    }

    finishSwipe(event) {
        if (!this.swipeStart || this.zoom > 1) {
            return
        }

        const touch = Array.from(event.changedTouches)
            .find(({ identifier }) => identifier === this.swipeStart.touchId)

        if (!touch) {
            return
        }

        const swipeStart = this.swipeStart
        this.cancelSwipe()

        const deltaX = touch.clientX - swipeStart.x

        if (swipeStart.direction !== 'horizontal' || Math.abs(deltaX) < SWIPE_DISTANCE_THRESHOLD) {
            return
        }

        // Moving the content left reveals the next page; moving right returns.
        if (deltaX < 0) {
            void this.showNextPages('swipe')
        } else {
            void this.showPreviousPages('swipe')
        }
    }

    cancelSwipe() {
        this.swipeStart = null
    }

    handleKeydown(event) {
        // Removing tabindex after an error does not remove this listener or necessarily move focus.
        if (!this.isReady || this.hasError || this.isRendering) {
            return
        }

        // Leave modified shortcuts to the browser/assistive technology. While zoomed,
        // unmodified arrows also stay native so they scroll instead of turning pages.
        if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || this.zoom > 1) {
            return
        }

        if (event.key === 'ArrowLeft') {
            event.preventDefault()
            void this.showPreviousPages('previous')
        } else if (event.key === 'ArrowRight') {
            event.preventDefault()
            void this.showNextPages('next')
        }
    }

    async showPreviousPages(navigationMethod) {
        if (!this.isReady || this.hasError || this.isRendering || this.currentPage === 1) {
            return
        }

        if (this.isDesktopLayout()) {
            const firstVisiblePage = this.getFirstVisiblePageNumber()

            this.currentPage = firstVisiblePage <= 2 ? 1 : firstVisiblePage - 2
        } else {
            this.currentPage -= 1
        }

        this.transitionDirection = 'previous'
        this.scrollResetPending = true
        if (await this.render()) {
            this.emitPageChange(navigationMethod)
        }
    }

    async showNextPages(navigationMethod) {
        if (!this.isReady || this.hasError || this.isRendering) {
            return
        }

        const lastVisiblePage = this.getVisiblePageNumbers().at(-1)

        if (lastVisiblePage >= this.pageCount) {
            return
        }

        if (this.isDesktopLayout() && this.currentPage > 1) {
            this.currentPage = Math.min(
                this.getFirstVisiblePageNumber() + 2,
                this.pageCount,
            )
        } else {
            this.currentPage += 1
        }

        this.transitionDirection = 'next'
        this.scrollResetPending = true
        if (await this.render()) {
            this.emitPageChange(navigationMethod)
        }
    }

    updateControls() {
        const lastVisiblePage = this.getVisiblePageNumbers().at(-1)
        const disabled = this.isRendering || !this.isReady || this.hasError

        if (this.previousButton) {
            this.previousButton.disabled = disabled || this.currentPage === 1
        }

        if (this.nextButton) {
            this.nextButton.disabled = disabled || lastVisiblePage >= this.pageCount
        }
        // Hide pointer affordances while rendering, zoomed, or otherwise unable to navigate.
        this.root.toggleAttribute(
            'data-pdf-canvas-navigation',
            !disabled && this.zoom === 1 && this.pageCount > 1,
        )
        if (this.zoomInButton) this.zoomInButton.disabled = disabled || this.zoom >= MAX_ZOOM
        if (this.zoomOutButton) this.zoomOutButton.disabled = disabled || this.zoom <= 1
        if (this.fitButton) this.fitButton.disabled = disabled || this.zoom === 1
        if (this.zoomLevel) this.zoomLevel.textContent = `${Math.round(this.zoom * 100)}%`
    }

    updateStatus(pageNumbers) {
        // Retain the loading announcement until every logical page has been counted.
        if (this.pageCount === 0) {
            return
        }

        const pageLabel = pageNumbers.length === 1
            ? String(pageNumbers[0])
            : `${pageNumbers[0]}-${pageNumbers.at(-1)}`

        if (this.currentPageElement) {
            this.currentPageElement.textContent = pageLabel
        }

        if (this.pageCountElement) {
            this.pageCountElement.textContent = String(this.pageCount)
        }

        if (this.status) {
            this.status.textContent = `Page ${pageLabel} of ${this.pageCount}. Zoom ${Math.round(this.zoom * 100)}%.`
        }
    }

    showError() {
        this.cancelGestures()
        this.root.removeAttribute('data-pdf-zoomed')
        this.root.removeAttribute('data-pdf-canvas-navigation')
        this.hasError = true
        if (this.zoomControls) this.zoomControls.hidden = true
        this.root.setAttribute('aria-busy', 'false')
        // Do not advertise keyboard controls when the viewer can no longer navigate.
        this.viewport.removeAttribute('aria-describedby')
        this.viewport.removeAttribute('aria-keyshortcuts')
        this.viewport.removeAttribute('tabindex')

        if (this.loading) {
            this.loading.textContent = 'Unable to display the PDF. Use the Download PDF link below to access the original document.'
            this.loading.hidden = false
        }

        this.pages.hidden = true

        if (this.controls) {
            this.controls.hidden = true
        }

        if (this.status) {
            this.status.textContent = 'Unable to display the PDF.'
        }
    }
}

const pdfFlipbook = {
    init() {
        document.querySelectorAll('[data-pdf-flipbook]').forEach((root) => {
            if (root.dataset.pdfFlipbookInit === 'true') {
                return
            }

            root.dataset.pdfFlipbookInit = 'true'
            new PdfFlipbook(root).init()
        })
    },
}

export default pdfFlipbook
